import {
  useCallback, useEffect, useMemo, useState,
} from 'react'
import { actions } from '../../PrintersManagementState'
import { useDispatch } from 'react-redux'
import { useSelector } from 'src/utils/store'
import { CircularProgress, Tooltip, Typography } from '@mui/material'
import { PrinterData } from '../../PrintersManagementTypes'
import { toast } from 'react-toastify'
import TableMui from 'src/components/TableMui'
import * as S from './RemotePrintersList.styles'
import CachedIcon from '@mui/icons-material/Cached'

interface RemotePrintersListProps {
  fulfillmentCenterId: number
  height?: string
  characterLimitNumber?: number
}

export const RemotePrintersList = ({
  fulfillmentCenterId, height = '40vh', characterLimitNumber = 50,
}: RemotePrintersListProps) => {
  const [savedPrinterInfo, setSavedPrinterInfo] = useState<PrinterData>()
  const [preventDoubleClick, setPreventDoubleClick] = useState(false)
  const { printers } = useSelector((state) => state.printersManagement)
  const dispatch = useDispatch()

  useEffect(() => {
    const printerInfo = localStorage.getItem('printerInfo')
    if (printerInfo) setSavedPrinterInfo(JSON.parse(printerInfo))
  }, [])

  const onSelect = useCallback((printer: PrinterData) => {
    if (preventDoubleClick) return

    setPreventDoubleClick(true)

    dispatch(actions.checkRemotePrinterHealth({
      ip: printer.localIp,
      onSuccess: () => {
        localStorage.setItem('printerInfo', JSON.stringify(printer))
        setSavedPrinterInfo(printer)
        toast.success('Impressora selecionada com sucesso!')
        setPreventDoubleClick(false)
      },
      onError: () => setPreventDoubleClick(false),
    }))
  }, [dispatch, preventDoubleClick])

  const resolveTableRows = useMemo(() => printers.map((printer) => ({
    ...printer,
    action: (
      <S.CustomActionButton
        title={preventDoubleClick ? <CircularProgress size={26} sx={{ color: '#FFF' }}/> : 'Selecionar'}
        bgColor='lightBlue5'
        hoverColor='darkBlueHover2'
        letterColor='white'
        data-testid='select-printer-button-test-id'
        onClick={() => onSelect(printer)}
        disabled={preventDoubleClick}
      />
    ),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [onSelect, printers])

  useEffect(() => {
    if (fulfillmentCenterId) {
      dispatch(actions.getActivePrinters({
        fulfillmentCenterId,
        onSuccess: () => null,
        onError: () => null,
      }))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fulfillmentCenterId])

  const handleRemovePrinter = () => {
    localStorage.removeItem('printerInfo')
    setSavedPrinterInfo(undefined)
  }

  const handleUpdateActivePrinters = () => {
    if (preventDoubleClick) {
      return
    }

    setPreventDoubleClick(true)

    dispatch(actions.getActivePrinters({
      fulfillmentCenterId,
      onSuccess: () => setPreventDoubleClick(false),
      onError: () => setPreventDoubleClick(false),
    }))
  }

  return fulfillmentCenterId ? (
    <S.Container>
      <S.ContainerTitle>
        <Typography variant='h5'>Impressoras online nas últimas 24 horas</Typography>
        <Tooltip title='ATUALIZAR IMPRESSORAS ONLINE' placement='top' arrow>
          <CachedIcon
            onClick={handleUpdateActivePrinters}
            fontSize='large'
            sx={{
              cursor: 'pointer',
              color: 'rgba(0, 0, 0, 0.54)',
            }}
            data-testid='update-active-printers-test-id'
          />
        </Tooltip>
      </S.ContainerTitle>
      <S.ContainerSelectedPrinter>
        <S.CustomTypography hasSavedPrinter={!!savedPrinterInfo} variant='h6'>
          {savedPrinterInfo ? `Utilizando: ${savedPrinterInfo.deviceName} (${savedPrinterInfo.macAddress})` : 'Nenhuma impressora selecionada'}
        </S.CustomTypography>
        <S.CustomButton
          title='Remover Impressora'
          bgColor='red'
          hoverColor='redHover'
          letterColor='white'
          data-testid='remove-selected-printer-button-test-id'
          onClick={handleRemovePrinter}
          disabled={!savedPrinterInfo}
        />
      </S.ContainerSelectedPrinter>
      <TableMui
        columns={[
          { id: 'deviceName', label: 'Nome da impressora', limitTextAndShowInTooltip: characterLimitNumber },
          { id: 'macAddress', label: 'Endereço MAC' },
          { id: 'lastActivity', label: 'Última atualização', isDate: true },
          { id: 'action', label: 'Ação', useSorting: false },
        ]}
        emptyLabel='Nenhuma impressora encontrada'
        rows={resolveTableRows}
        enablePagination
        rowsPerPageLimit={10}
        maxHeight={height}
        minHeight={height}
        hasSorting
        testId='remote-printers-table-test-id'
      />
    </S.Container>
  ) : null
}
